import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  ArrowLeft,
  Receipt,
  Building2,
  Calendar,
  Users,
  Clock,
  Euro,
  Car,
  Home,
  Package,
  FileText,
  CheckCircle,
  Loader2,
  Copy,
  Download,
  Lock,
  AlertTriangle,
  X,
  PanelRightOpen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = '/api';
const AUTO_SAVE_DELAY = 500;

// Save Status Indicator
function SaveStatus({ status }) {
  if (status === "idle") return null;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
      status === "saving" 
        ? "bg-blue-50 text-blue-700 border border-blue-200" 
        : status === "saved" 
          ? "bg-green-50 text-green-700 border border-green-200"
          : status === "error"
            ? "bg-red-50 text-red-700 border border-red-200"
            : ""
    }`} data-testid="save-status">
      {status === "saving" && (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Speichern...</span>
        </>
      )}
      {status === "saved" && (
        <>
          <CheckCircle className="w-4 h-4" />
          <span>Gespeichert</span>
        </>
      )}
    </div>
  );
}

export default function AbrechnungDetail() {
  const { abrechnungId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [abrechnung, setAbrechnung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [exporting, setExporting] = useState(false);
  
  // Summary panel visibility - default closed on smaller screens
  const [showSummary, setShowSummary] = useState(() => {
    // Check if we're on a larger screen (>= 1280px)
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1280;
    }
    return true;
  });
  
  // Auto-save refs
  const autoSaveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef(null);

  // Check permission
  useEffect(() => {
    if (user && !user?.permissions?.can_manage_users) {
      navigate("/app");
      toast.error("Nur Admins können Abrechnungen verwalten");
    }
  }, [user, navigate]);

  // Load abrechnung
  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/abrechnungen/${abrechnungId}`);
        if (res.ok) {
          const data = await res.json();
          setAbrechnung(data);
        } else {
          toast.error("Abrechnung nicht gefunden");
          navigate("/app/abrechnungen");
        }
      } catch (error) {
        toast.error("Fehler beim Laden");
        navigate("/app/abrechnungen");
      } finally {
        setLoading(false);
      }
    };
    
    if (abrechnungId) {
      load();
    }
  }, [abrechnungId, navigate]);

  // Auto-save function
  const performAutoSave = useCallback(async (dataToSave) => {
    if (!dataToSave || abrechnung?.status === "Exportiert") return;
    
    setSaveStatus("saving");
    
    try {
      const res = await authFetch(`${API}/abrechnungen/${abrechnungId}`, {
        method: "PUT",
        body: JSON.stringify({
          unterkunft_betrag: dataToSave.unterkunft_betrag,
          material_betrag: dataToSave.material_betrag,
          taetigkeit_text: dataToSave.taetigkeit_text,
          km_pauschale: dataToSave.km_pauschale,
        }),
      });
      
      if (res.ok) {
        const updated = await res.json();
        setAbrechnung(updated);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        setSaveStatus("error");
        toast.error("Fehler beim Speichern");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch (error) {
      setSaveStatus("error");
      toast.error("Fehler beim Speichern");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [abrechnungId, abrechnung?.status]);

  // Trigger auto-save with debounce
  const triggerAutoSave = useCallback((newData) => {
    pendingChangesRef.current = newData;
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave(pendingChangesRef.current);
    }, AUTO_SAVE_DELAY);
  }, [performAutoSave]);

  // Update field
  const updateField = (field, value) => {
    setAbrechnung(prev => {
      const newData = { ...prev, [field]: value };
      
      // Recalculate gesamtsumme locally for immediate feedback
      const unterkunft = field === "unterkunft_betrag" ? value : prev.unterkunft_betrag;
      const material = field === "material_betrag" ? value : prev.material_betrag;
      const kmPauschale = field === "km_pauschale" ? value : prev.km_pauschale;
      const gesamt_km = prev.gesamt_km_hin + prev.gesamt_km_rueck;
      const km_summe = gesamt_km * kmPauschale;
      const gesamtsumme = prev.stundensumme + km_summe + (parseFloat(unterkunft) || 0) + (parseFloat(material) || 0);
      
      newData.km_summe = km_summe;
      newData.gesamtsumme = gesamtsumme;
      
      triggerAutoSave(newData);
      return newData;
    });
  };

  // Export as finished
  const handleExport = async () => {
    if (!confirm("Abrechnung als exportiert markieren? Die zugehörigen Zeiteinträge werden gesperrt.")) return;
    
    setExporting(true);
    try {
      const res = await authFetch(`${API}/abrechnungen/${abrechnungId}/export`, { method: "POST" });
      if (res.ok) {
        toast.success("Abrechnung exportiert");
        // Reload to get updated status
        const updated = await authFetch(`${API}/abrechnungen/${abrechnungId}`);
        if (updated.ok) {
          setAbrechnung(await updated.json());
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Fehler beim Exportieren");
      }
    } catch (error) {
      toast.error("Fehler beim Exportieren");
    } finally {
      setExporting(false);
    }
  };

  // Copy Lexoffice text
  const handleCopyLexoffice = async () => {
    try {
      const res = await authFetch(`${API}/abrechnungen/${abrechnungId}/lexoffice-text`);
      if (res.ok) {
        const { text } = await res.json();
        await navigator.clipboard.writeText(text);
        toast.success("Text für Lexoffice kopiert!");
      } else {
        toast.error("Fehler beim Erstellen des Texts");
      }
    } catch (error) {
      toast.error("Fehler beim Kopieren");
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!abrechnung) {
    return (
      <div className="text-center py-24">
        <p className="text-gray-500">Abrechnung nicht gefunden</p>
      </div>
    );
  }

  const isExported = abrechnung.status === "Exportiert";

  return (
    <div className="space-y-6" data-testid="abrechnung-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/abrechnungen")}
            className="gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <SaveStatus status={saveStatus} />
          
          {/* Show Summary Button - visible when summary is hidden */}
          {!showSummary && (
            <Button
              variant="outline"
              onClick={() => setShowSummary(true)}
              className="gap-2"
              data-testid="show-summary-btn"
            >
              <PanelRightOpen className="w-4 h-4" />
              Zusammenfassung anzeigen
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={handleCopyLexoffice}
            className="gap-2"
            data-testid="copy-lexoffice-btn"
          >
            <Copy className="w-4 h-4" />
            Text für Lexoffice kopieren
          </Button>
          
          {!isExported && (
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="export-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Als exportiert markieren
            </Button>
          )}
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await authFetch(`${API}/abrechnungen/${abrechnungId}/pdf`);
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `Abrechnung_${abrechnung?.projekt_name || "Export"}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("PDF heruntergeladen");
                } else {
                  toast.error("Fehler beim PDF-Export");
                }
              } catch { toast.error("PDF-Export fehlgeschlagen"); }
            }}
            className="gap-2"
            data-testid="pdf-export-btn"
          >
            <FileText className="w-4 h-4" />
            PDF herunterladen
          </Button>
        </div>
      </div>

      {/* Exported Warning */}
      {isExported && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Lock className="w-5 h-5 text-green-600" />
          <div>
            <p className="font-medium text-green-800">Abrechnung exportiert</p>
            <p className="text-sm text-green-600">
              Exportiert am {format(new Date(abrechnung.exportiert_am), "dd.MM.yyyy HH:mm", { locale: de })}
            </p>
          </div>
        </div>
      )}

      {/* Project Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                isExported 
                  ? "bg-green-100 text-green-800 border border-green-300"
                  : "bg-yellow-100 text-yellow-800 border border-yellow-300"
              }`}>
                {abrechnung.status}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-[Manrope]">
              {abrechnung.projekt_name}
            </h1>
            {abrechnung.kunde_name && (
              <p className="text-gray-500 flex items-center gap-2 mt-1">
                <Building2 className="w-4 h-4" />
                {abrechnung.kunde_name}
              </p>
            )}
          </div>
          
          <div className="text-right">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              {abrechnung.zeitraum_von} – {abrechnung.zeitraum_bis}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Erstellt von {abrechnung.erstellt_von_name}
            </p>
          </div>
        </div>
      </div>

      <div className={`grid gap-6 transition-all duration-300 ease-in-out ${showSummary ? "grid-cols-1 xl:grid-cols-3" : "grid-cols-1"}`}>
        {/* Left: Calculations */}
        <div className={`space-y-6 transition-all duration-300 ease-in-out ${showSummary ? "col-span-1 xl:col-span-2" : "col-span-1"}`}>
          {/* Personnel */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Personal ({abrechnung.personal_anzahl} Mitarbeiter)
            </h2>
            
            <div className="space-y-3">
              {abrechnung.mitarbeiter_details?.map((ma, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{ma.user_name}</p>
                    <p className="text-sm text-gray-500">
                      {ma.stunden.toFixed(2)}h × {ma.stundensatz.toFixed(2)} €/h
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {ma.teilbetrag.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              ))}
              
              {/* Stundensumme */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">Arbeitsstunden Summe</span>
                </div>
                <p className="font-bold text-blue-800">
                  {abrechnung.stundensumme.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                </p>
              </div>
            </div>
          </div>

          {/* Fahrtkosten */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5" />
              Fahrtkosten
            </h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Anfahrt</p>
                <p className="font-medium text-gray-900">
                  {abrechnung.gesamt_km_hin.toFixed(1)} km
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Abfahrt</p>
                <p className="font-medium text-gray-900">
                  {abrechnung.gesamt_km_rueck.toFixed(1)} km
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <Label className="text-gray-700">km-Pauschale (€/km)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={abrechnung.km_pauschale || ""}
                onChange={(e) => updateField("km_pauschale", parseFloat(e.target.value) || 0)}
                disabled={isExported}
                className="mt-1"
                data-testid="input-km-pauschale"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-800">Fahrtkosten Summe</span>
              </div>
              <p className="font-bold text-blue-800">
                {abrechnung.km_summe.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
              </p>
            </div>
          </div>

          {/* Zusatzkosten */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Zusatzkosten
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 text-gray-700">
                  <Home className="w-4 h-4" />
                  Unterkunft (€)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={abrechnung.unterkunft_betrag || ""}
                  onChange={(e) => updateField("unterkunft_betrag", parseFloat(e.target.value) || 0)}
                  disabled={isExported}
                  className="mt-1"
                  data-testid="input-unterkunft"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-gray-700">
                  <Package className="w-4 h-4" />
                  Material (€)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={abrechnung.material_betrag || ""}
                  onChange={(e) => updateField("material_betrag", parseFloat(e.target.value) || 0)}
                  disabled={isExported}
                  className="mt-1"
                  data-testid="input-material"
                />
              </div>
            </div>
          </div>

          {/* Tätigkeit */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Tätigkeit
            </h2>
            
            <Textarea
              value={abrechnung.taetigkeit_text || ""}
              onChange={(e) => updateField("taetigkeit_text", e.target.value)}
              disabled={isExported}
              placeholder="Beschreibung der durchgeführten Tätigkeiten..."
              className="min-h-[120px]"
              data-testid="input-taetigkeit"
            />
          </div>
        </div>

        {/* Right: Summary - Collapsible */}
        {showSummary && (
          <div className="space-y-6 animate-in slide-in-from-right duration-300">
            {/* Gesamtsumme */}
            <div className="bg-gray-900 text-white rounded-xl p-6 shadow-lg sticky top-32 relative">
              {/* Close Button */}
              <button
                onClick={() => setShowSummary(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                title="Zusammenfassung schließen"
                data-testid="close-summary-btn"
              >
                <X className="w-4 h-4" />
              </button>
              
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 pr-8">
                <Euro className="w-5 h-5" />
                Zusammenfassung
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>Arbeitsstunden</span>
                  <span>{abrechnung.stundensumme.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Fahrtkosten</span>
                  <span>{abrechnung.km_summe.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Unterkunft</span>
                  <span>{(abrechnung.unterkunft_betrag || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Material</span>
                  <span>{(abrechnung.material_betrag || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                </div>
                
                <div className="border-t border-gray-700 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Gesamtsumme</span>
                    <span className="text-2xl font-bold">
                      {abrechnung.gesamtsumme.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-700">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Users className="w-4 h-4" />
                  <span>{abrechnung.personal_anzahl} Mitarbeiter</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                  <Clock className="w-4 h-4" />
                  <span>{abrechnung.gesamtstunden.toFixed(1)} Stunden</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                  <Car className="w-4 h-4" />
                  <span>{(abrechnung.gesamt_km_hin + abrechnung.gesamt_km_rueck).toFixed(1)} km</span>
                </div>
              </div>
            </div>

            {/* Included Time Entries Count */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600">
                <FileText className="w-4 h-4" />
                <span className="text-sm">
                  {abrechnung.zeiteintraege_ids?.length || 0} Zeiteinträge enthalten
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
